import { __rest } from "tslib";
import { animatableKeys } from 'shared/config';
import { removeUndefinedValues } from 'shared/utils';
/**
 * Create a AnimeJS transition between two groups of styles.
 */
export const buildTransition = (targets, stylesIn, stylesOut, offset = 0, duration = 0, easing = 'linear') => {
    const styleTransitions = Object.keys(stylesIn)
        .filter(key => 
    // Strip out non-animatable styles.
    animatableKeys.indexOf(key) !== -1 &&
        // Only values that exist in both style groups.
        Object.keys(stylesOut).indexOf(key) !== -1 &&
        // No sense in animating values that don't change.
        stylesIn[key] !== stylesOut[key])
        // Always, include `rotation` (even if it isn't animated),
        // as it affects other transforms.
        .concat('rotate')
        // Map all styles to in-out tuples.
        .reduce((carrier, key) => (Object.assign(Object.assign({}, carrier), { [key]: [pickStyle(stylesIn, key), pickStyle(stylesOut, key)] })), {});
    return Object.assign({ targets,
        duration,
        easing,
        offset }, reorderTransformTransitions(styleTransitions));
};
/**
 * Gets a single style value from Styles. Ensures that
 * `rotate` always receives a fall-back value.
 */
const pickStyle = (styles, key) => key === 'rotate' ? styles[key] || '0deg' : styles[key];
/**
 * Ensure animatedKeys in correct order for transform string
 * CSS3 transform order matters: rightmost operation first.
 */
export const reorderTransformTransitions = (transitions) => {
    const { translateX, translateY, rotate, scaleX, scaleY } = transitions, rest = __rest(transitions, ["translateX", "translateY", "rotate", "scaleX", "scaleY"]);
    return removeUndefinedValues(Object.assign({ translateX,
        translateY,
        rotate,
        scaleX,
        scaleY }, rest));
};
/**
 * Create selector for timeline animation based on symbol id
 */
export const getAnimeRefId = (symbolId) => `ani-${symbolId}`;
/**
 * Offset transforms so that:
 *
 * (a) Translates account for scale
 * (b) Animation rotation is relative to the reset rotation.
 */
export const offsetTransforms = (styles, originalStyle) => {
    const { translateX, translateY, rotate, scaleX, scaleY } = styles, rest = __rest(styles, ["translateX", "translateY", "rotate", "scaleX", "scaleY"]);
    const { rotate: restRotate } = originalStyle;
    const relativeRotate = calculateRelativeRotation(rotate, restRotate);
    const offsetTranslateX = calculateOffsetTranslate(translateX, scaleX);
    const offsetTranslateY = calculateOffsetTranslate(translateY, scaleY);
    return removeUndefinedValues(Object.assign({ translateX: offsetTranslateX, translateY: offsetTranslateY, rotate: relativeRotate, scaleX,
        scaleY }, rest));
};
/**
 * Adjust rotation so that it is relative to the original, rest rotation.
 */
export const calculateRelativeRotation = (rotate = 0, restRotate = 0) => `${parseFloat(`${restRotate}`) + parseFloat(`${rotate}`)}deg`;
/**
 * Adjust a translate so that it accounts for scale.
 */
export const calculateOffsetTranslate = (translate, scale = 1) => {
    const scaleNumber = parseFloat(`${scale}`);
    // No translate or scale, so omit the value all together.
    if (typeof translate === 'undefined' && scale === 1) {
        return undefined;
    }
    const translateNumber = translate ? parseFloat(`${translate}`) : 0;
    const scaleOffset = ((1 - scaleNumber) / 2) * 100;
    const cleanScaleOffset = +scaleOffset.toFixed(1);
    return `${translateNumber - cleanScaleOffset}%`;
};
